import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form, Input, Modal, Switch } from 'antd'
import { ModalProps } from 'antd/lib'
import { useEffect } from 'react'

import {
  useStocksServiceCreateInternationalNameEndpoint,
  useStocksServiceGetInternationalNameEndpoint,
  useStocksServiceGetInternationalNameListEndpointKey,
  useStocksServiceUpdateSubstanceEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { message, SaveConfirm } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

const getModalText = (hasId: boolean) => {
  return {
    title: `medicine_register.international_name.${hasId ? 'edit' : 'add'}_name`,
    button: `common.${hasId ? 'edit' : 'add'}`
  }
}

export const InternationalNamesModal = ({ id, onCancel, ...props }: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const { title, button } = getModalText(!!id)

  const { data } = useStocksServiceGetInternationalNameEndpoint(
    { id: String(id) },
    [id],
    { enabled: !!id }
  )

  const { mutate: create, isPending: isCreating } =
    useStocksServiceCreateInternationalNameEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => onSuccessMutation()
    })

  const { mutate: edit, isPending: isEditing } =
    useStocksServiceUpdateSubstanceEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => onSuccessMutation()
    })

  const onSubmit = async () => {
    const requestBody = await form.validateFields()

    const payload = {
      name: requestBody.name,
      dosage: `${requestBody.dosage} mg`,
      isUsed: requestBody.isUsed
    }

    id ? edit({ requestBody: payload, id }) : create({ requestBody: payload })
  }

  const onSuccessMutation = () => {
    message.success(t('common.save_success'))
    queryClient.invalidateQueries({
      queryKey: [useStocksServiceGetInternationalNameListEndpointKey]
    })
    onCancel()
  }

  useEffect(() => {
    if (data && id) form.setFieldsValue(data)
  }, [data, id])

  return (
    <Modal
      title={t(title)}
      open
      centered
      footer={null}
      onCancel={onCancel}
      maskClosable={false}
      afterClose={form.resetFields}
      {...props}
    >
      <Flex vertical gap={24}>
        <Form form={form} layout="vertical" requiredMark={false}>
          <Flex vertical gap={24}>
            <Form.Item
              name="name"
              rules={requiredRules}
              label={t('medicine_register.international_name.name')}
            >
              <Input
                placeholder={t('medicine_register.international_name.name')}
              />
            </Form.Item>

            <Form.Item
              name="dosage"
              label={t('treatments.medication_dose')}
              rules={requiredRules}
            >
              <Input placeholder={t('treatments.medication_dose')} />
            </Form.Item>
            <Form.Item
              valuePropName="checked"
              name="isUsed"
              label={t('medicine_register.international_name.is_used')}
            >
              <Switch />
            </Form.Item>
          </Flex>
        </Form>

        <Flex justify="center" gap={16}>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>

          <SaveConfirm
            title={`${t(button)}?`}
            onConfirm={onSubmit}
            okButtonProps={{ loading: isCreating || isEditing }}
          >
            <Button type="primary">{t(button)}</Button>
          </SaveConfirm>
        </Flex>
      </Flex>
    </Modal>
  )
}
